/**
 * `BucketModal` — opens when a user clicks a cell in the suite-level uptime
 * bar. Surfaces the per-endpoint status at that exact bucket so the operator
 * can answer "what was failing at 12:47?" without expanding the card and
 * scrolling. Same status grammar (dot + label) as the rest of the page.
 */
import clsx from 'clsx';
import { useRef } from 'react';
import type { BucketSeverity } from '../lib/bucket-severity';
import { parseEndpointKey } from '../lib/endpoint-key';
import { ModalCloseButton, ModalShell } from './ModalShell';

/**
 * The header dot reports the day's SEVERITY, so it speaks the same vocabulary
 * as the cell the user clicked (`lib/bucket-severity.ts`) — otherwise a green
 * 99.99% day would open a modal with a red dot, re-asserting the very verdict
 * the ladder exists to retire.
 *
 * The ROWS keep the raw backend status on purpose: this list is the evidence
 * ("what actually failed that day"), sorted worst-first, and an endpoint that
 * failed once should be visible here even though the day as a whole was fine.
 */
export type BucketModalStatus = BucketSeverity;

export interface BucketModalRow {
  endpointKey: string;
  status: BucketModalStatus;
}

export interface BucketModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  overall?: BucketModalStatus;
  rows: readonly BucketModalRow[];
}

const TONE_DOT_LG: Record<BucketModalStatus, string> = {
  ok: 'bg-ok shadow-[0_0_0_3px_rgba(26,166,107,.18)]',
  warn: 'bg-warn shadow-[0_0_0_3px_rgba(224,160,25,.2)]',
  major: 'bg-major shadow-[0_0_0_3px_rgba(242,135,47,.2)]',
  down: 'bg-down shadow-[0_0_0_3px_rgba(227,80,62,.2)]',
  no_data: 'bg-line-strong',
};

const TONE_DOT_SM: Record<BucketModalStatus, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  major: 'bg-major',
  down: 'bg-down',
  no_data: 'bg-line-strong',
};

const TONE_LABEL: Record<BucketModalStatus, string> = {
  ok: 'Operational',
  warn: 'Degraded',
  major: 'Heavily degraded',
  down: 'Outage',
  no_data: 'No data',
};

const TONE_TEXT: Record<BucketModalStatus, string> = {
  ok: 'text-ok-fg',
  warn: 'text-warn-fg',
  major: 'text-major-fg',
  down: 'text-down-fg',
  no_data: 'text-ink-3',
};

const STATUS_RANK: Record<BucketModalStatus, number> = {
  down: 0,
  major: 1,
  warn: 2,
  ok: 3,
  no_data: 4,
};

/**
 * Order rows so outages surface first: down → warn → ok → no_data. Stable
 * within a status (preserves the original endpoint order). Returns a copy —
 * never mutates the input.
 */
export function sortBucketRows(
  rows: readonly BucketModalRow[],
): BucketModalRow[] {
  return [...rows].sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status]);
}

export function BucketModal({
  isOpen,
  onClose,
  title,
  subtitle,
  overall,
  rows,
}: BucketModalProps) {
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  if (!isOpen) return null;

  const counts = rows.reduce(
    (acc, r) => {
      acc[r.status] += 1;
      return acc;
    },
    { ok: 0, warn: 0, down: 0, no_data: 0 } as Record<
      BucketModalStatus,
      number
    >,
  );

  return (
    <ModalShell
      onClose={onClose}
      labelledById="bucket-modal-title"
      initialFocusRef={closeBtnRef}
    >
      <header className="flex items-start gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              {overall && (
                <span
                  className={clsx(
                    'inline-block h-[8px] w-[8px] shrink-0 rounded-full',
                    TONE_DOT_LG[overall],
                  )}
                />
              )}
              <h2
                id="bucket-modal-title"
                className="m-0 text-[15px] font-semibold tracking-[-.01em] text-ink-1"
              >
                {title}
              </h2>
            </div>
            {subtitle && (
              <p className="m-0 mt-1 font-mono text-[11.5px] tracking-[-.01em] text-ink-3">
                {subtitle}
              </p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-ink-3 [font-feature-settings:'tnum']">
              {counts.ok > 0 && (
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-ok" />
                  {counts.ok} ok
                </span>
              )}
              {counts.warn > 0 && (
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-warn" />
                  {counts.warn} warn
                </span>
              )}
              {counts.down > 0 && (
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-down" />
                  {counts.down} down
                </span>
              )}
              {counts.no_data > 0 && (
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-line-strong" />
                  {counts.no_data} no data
                </span>
              )}
            </div>
          </div>
          <ModalCloseButton onClose={onClose} buttonRef={closeBtnRef} />
        </header>
        <ul className="min-h-0 flex-1 divide-y divide-line overflow-y-auto">
          {rows.length === 0 ? (
            <li className="px-5 py-6 text-center text-[13px] text-ink-3">
              No endpoint data for this slice.
            </li>
          ) : (
            sortBucketRows(rows).map((row) => (
              <ModalRow key={row.endpointKey} row={row} />
            ))
          )}
        </ul>
    </ModalShell>
  );
}

function ModalRow({ row }: { row: BucketModalRow }) {
  const parsed = parseEndpointKey(row.endpointKey);
  return (
    <li className="flex items-center gap-3 px-5 py-2.5">
      <span
        className={clsx(
          'h-2 w-2 shrink-0 rounded-full',
          TONE_DOT_SM[row.status],
        )}
      />
      {parsed.method ? (
        <span className="shrink-0 rounded border border-line bg-paper-2 px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wide text-ink-2">
          {parsed.method}
        </span>
      ) : null}
      <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-ink-1">
        {parsed.path}
      </span>
      <span
        className={clsx(
          'shrink-0 whitespace-nowrap text-[12px] font-semibold',
          TONE_TEXT[row.status],
        )}
      >
        {TONE_LABEL[row.status]}
      </span>
    </li>
  );
}
